import type { FetchOptions } from 'ofetch'
import { FetchError } from 'ofetch'
import { check, instance, is, object, picklist, pipe, union } from 'valibot'

/**
 * Type guard to determine if an error is a FetchError with a specific status code or codes.
 *
 * FIXME: Should be a pure instanceof FetchError check, but for some reason that doesn't always work as expected.
 *        Likely related to this issue: https://github.com/nuxt/nuxt/issues/22753
 *
 * @param error - The error to check.
 * @param status - The status code or codes to check for.
 * @returns True if the error is a FetchError with the specified status code or codes, false otherwise.
 */
export function isFetchError(error: unknown, status: number | number[]): error is FetchError {
  return is(
    union([
      object({ status: picklist(Array.isArray(status) ? status : [status]) }),
      pipe(
        instance(FetchError),
        check(err => Array.isArray(status) ? status.includes(err.statusCode || err.status || 0) : (err.statusCode === status || err.status === status), `FetchError with status ${status}`),
      ),
    ]),
    error,
  )
}

/**
 * A timed-out request has no status, so `isFetchError` cannot identify it. ofetch
 * exposes the abort reason through the error's `cause` instead.
 */
export function isTimeoutError(error: unknown): boolean {
  return is(
    union([
      pipe(
        object({ cause: instance(Error) }),
        check((obj) => obj.cause.name === 'TimeoutError', 'TimeoutError'),
      ),
      pipe(
        instance(FetchError),
        check(err => err.cause instanceof Error && err.cause.name === 'TimeoutError', 'TimeoutError'),
      ),
    ]),
    error,
  )
}

/**
 * Converts 404 responses to 204 No Content responses. Useful for APIs that serve resources that may not exist, but we don't want to treat as errors.
 */
export const convert404to204 = ((ctx) => {
  if (ctx.response?.status === 404) {
    ctx.response = new Response(null, { status: 204 })
  }
}) satisfies FetchOptions['onResponse']
